/* eslint-disable no-plusplus */
/* eslint-disable import/no-extraneous-dependencies */
import React, { useEffect, useState } from "react";
import { IoClose } from "react-icons/io5";
import { RiEditLine } from "react-icons/ri";
import { zodResolver } from "@hookform/resolvers/zod";
import { SubmitErrorHandler, useFieldArray, useForm } from "react-hook-form";
import { toast } from "react-toastify";
import {
  BooleanOptions,
  fName,
  quantidadeFilhosOptions,
  rNS,
  SexoBiologico,
  SexoBiologicoOptions,
} from "screens/perfilAutonomo/utils";
import { putDadosPessoaisAutonomo } from "screens/perfilAutonomo/api/putDadosPessoaisAutonomo";
import { putAutonomoContatoEmergencia } from "screens/perfilAutonomo/api/putAutonomoContatoEmergencia";
import * as S from "./styles";
import * as Styles from "../modal.styles";
import { DadosPessoaisSchema, schema } from "./schemas";
import { TextInputMask } from "../TextInputMask";
import TextInputZod from "../TextInputZod/textInputZod.component";
import { SelectComponent } from "../SelectComponent";
import { TextInput } from "../TextInput";
import { DatePicker } from "../DatePicker";
import { SecondaryButton } from "../secondaryButton.component";
import { PrimaryButton } from "../primaryButton.component";
import { Loading } from "../Loading";

export const DadosPessoaisModal = ({
  isOpen,
  onClose,
  user,
  authToken,
  refetch,
}: {
  isOpen: boolean;
  onClose: () => void;
  user: any;
  authToken: string;
  refetch: () => void;
}) => {
  const [saving, setSaving] = useState(false);

  const {
    register,
    control,
    handleSubmit,
    setValue,
    getValues,
    watch,
    formState: { errors },
  } = useForm<DadosPessoaisSchema>({
    resolver: zodResolver(schema),
    defaultValues: {
      celularComercial: "",
      celularPessoal: "",
      emailPessoal: "",
      nomeEmergencia: "",
      telefoneEmergencia: "",
      temFilhos: undefined,
      quantidadeFilhos: undefined,
      filhos: [],
    },
  });

  const {
    fields: filhosFields,
    append,
    remove,
  } = useFieldArray({
    control,
    name: "filhos",
  });

  const isClt = user?.isPj === false;

  const celularComercialValue = watch("celularComercial");
  const celularPessoalValue = watch("celularPessoal");
  const telefoneEmergenciaValue = watch("telefoneEmergencia");
  const temFilhosValue = watch("temFilhos");
  const quantidadeFilhosValue = watch("quantidadeFilhos");
  const filhosValue = watch("filhos");

  useEffect(() => {
    if (!temFilhosValue && typeof temFilhosValue === "boolean") {
      setValue("quantidadeFilhos", undefined);
      setValue("filhos", []);
      return;
    }

    const currentFields = filhosFields.length;
    const targetFields =
      quantidadeFilhosValue !== undefined &&
      typeof temFilhosValue === "boolean" &&
      temFilhosValue
        ? quantidadeFilhosValue
        : 0;

    if (currentFields < targetFields) {
      for (let i = currentFields; i < targetFields; i++) {
        append({
          nome: "",
          dataNascimento: null,
          sexo: undefined,
        });
      }
    } else if (currentFields > targetFields) {
      for (let i = currentFields; i > targetFields; i--) {
        remove(i - 1);
      }
    }
  }, [temFilhosValue, quantidadeFilhosValue, filhosFields.length]);

  useEffect(() => {
    if (isOpen) replaceValues();
  }, [isOpen]);

  const replaceValues = () => {
    setValue("celularComercial", user?.celularComercial);
    setValue("celularPessoal", user?.celularPessoal);
    setValue("emailPessoal", user?.emailContato);
    setValue("nomeEmergencia", user?.nomeEmergencia);
    setValue("telefoneEmergencia", user?.telefoneEmergencia);
    setValue("tipoContatoEmergencia", user?.tipoContatoEmergencia);
    setValue("temFilhos", user?.filhos?.length > 0);
    setValue("quantidadeFilhos", user?.filhos?.length ?? undefined);
    const filhos = user?.filhos
      ? user?.filhos.map((filho: any) => ({
          nome: filho?.nome,
          dataNascimento: new Date(filho?.dataNascimento),
          sexo: filho?.sexo,
        }))
      : [];
    setValue("filhos", filhos);
  };

  const onSubmitClt = () => {
    setSaving(true);

    putAutonomoContatoEmergencia({
      idAutonomo: user?.id,
      nomeEmergencia: getValues("nomeEmergencia"),
      telefoneEmergencia: getValues("telefoneEmergencia"),
      authToken,
    })
      .then(({ success }) => {
        if (success) {
          toast.success("Dados pessoais atualizados com sucesso!");
          refetch();
          onClose();
        } else throw new Error();
      })
      .catch((error) => {
        console.error(error);
        toast.error("Erro ao atualizar dados pessoais");
      })
      .finally(() => {
        setSaving(false);
      });
  };

  const onSubmit = (data: DadosPessoaisSchema) => {
    setSaving(true);

    putDadosPessoaisAutonomo({
      idAutonomo: user?.id,
      celularComercial: data.celularComercial,
      celular: data.celularPessoal,
      emailPessoal: data.emailPessoal,
      nomeEmergencia: data.nomeEmergencia,
      telefoneEmergencia: data.telefoneEmergencia,
      tipoContatoEmergencia: data.tipoContatoEmergencia,
      filhos: data.temFilhos ? data.filhos : null,
      authToken,
    })
      .then(({ success }) => {
        if (success) {
          toast.success("Dados pessoais atualizados com sucesso!");
          refetch();
          onClose();
        } else throw new Error();
      })
      .catch((error) => {
        console.error(error);
        toast.error("Erro ao atualizar dados pessoais");
      })
      .finally(() => {
        setSaving(false);
      });
  };

  const onError: SubmitErrorHandler<DadosPessoaisSchema> = (errors) => {
    if (Object.keys(errors)?.length > 0) {
      toast.error(
        "Por favor, verifique novamente os campos obrigatórios e dados inválidos."
      );
    }
  };

  return (
    <Styles.DialogContainer open={isOpen} onClose={onClose} maxWidth="md">
      <Styles.Container style={{ minWidth: 500 }}>
        <Styles.IconContainer>
          <div className="icon-wrapper">
            <RiEditLine color="#4CAF50" />
          </div>
          <h1 className="title center">{"Editar Dados Pessoais"}</h1>
          <div style={{ padding: 8 }}>
            <IoClose size={26} className="exit" onClick={onClose} />
          </div>
        </Styles.IconContainer>

        <S.Form>
          <S.Row>
            <S.Row>
              <S.Field>
                <p>
                  {"Celular Comercial "}
                  <span>*</span>
                </p>
                <TextInputMask
                  mask="99 99999-9999"
                  placeholder="00 00000-0000"
                  value={celularComercialValue}
                  onChange={(val) => setValue("celularComercial", rNS(val))}
                  disabled={isClt}
                  isError={!!errors?.celularComercial}
                />
              </S.Field>
              <S.Field>
                <p>{"Celular Pessoal"}</p>
                <TextInputMask
                  mask="99 99999-9999"
                  placeholder="00 00000-0000"
                  value={celularPessoalValue}
                  onChange={(val) => setValue("celularPessoal", rNS(val))}
                  disabled={isClt}
                  isError={!!errors?.celularPessoal}
                />
              </S.Field>
            </S.Row>
            <S.Field>
              <p>{"E-mail Pessoal"}</p>
              <TextInputZod
                register={register}
                property="emailPessoal"
                disabled={isClt}
                isError={!!errors?.emailPessoal}
              />
            </S.Field>
          </S.Row>

          <S.Row template="1fr 1fr 1fr">
            <S.Field>
              <p>
                {"Nome para Emergência "}
                <span>*</span>
              </p>
              <TextInputZod
                register={register}
                property="nomeEmergencia"
                isError={!!errors?.nomeEmergencia}
              />
            </S.Field>
            <S.Field>
              <p>
                {"Telefone para Emergência "}
                <span>*</span>
              </p>
              <TextInputMask
                mask="99 99999-9999"
                placeholder="00 00000-0000"
                value={telefoneEmergenciaValue}
                onChange={(val) => setValue("telefoneEmergencia", rNS(val))}
                isError={!!errors?.telefoneEmergencia}
              />
            </S.Field>
            <S.Field>
              <p>{"Parentesco"}</p>
              <TextInputZod
                register={register}
                property="tipoContatoEmergencia"
                isError={!!errors?.tipoContatoEmergencia}
              />
            </S.Field>
          </S.Row>

          <h2>{"Filhos"}</h2>

          <S.Row>
            <S.Field>
              <p>{"Tem Filhos?"}</p>
              <SelectComponent
                data={BooleanOptions}
                value={temFilhosValue}
                onChange={(val: boolean) => setValue("temFilhos", val)}
                isError={!!errors?.temFilhos}
                disabled={isClt}
                renderLabel
              />
            </S.Field>

            {temFilhosValue && typeof temFilhosValue === "boolean" ? (
              <S.Field>
                <p>
                  {"Quantos filhos? "}
                  <span>*</span>
                </p>
                <SelectComponent
                  data={quantidadeFilhosOptions}
                  value={quantidadeFilhosValue}
                  onChange={(val: number) => {
                    setValue("quantidadeFilhos", val);
                  }}
                  disabled={isClt}
                  isError={!!errors.quantidadeFilhos}
                />
              </S.Field>
            ) : (
              <div id="dummy" />
            )}
          </S.Row>

          {temFilhosValue &&
            typeof temFilhosValue === "boolean" &&
            quantidadeFilhosValue &&
            filhosFields.map((_, index) => (
              // eslint-disable-next-line react/no-array-index-key
              <S.Row template="1fr 1fr 1fr" key={index}>
                <S.Field>
                  <p>Nome do Filho</p>
                  <TextInput
                    placeholder="Ex.: Jeferson Fernandes"
                    value={filhosValue[index]?.nome}
                    onChange={(val) => {
                      setValue(`filhos.${index}.nome`, fName(val));
                    }}
                    disabled={isClt}
                    isError={!!errors.filhos && filhosValue[index]?.nome === ""}
                  />
                </S.Field>
                <S.Field>
                  <p>Data de Nascimento</p>
                  <DatePicker
                    value={filhosValue[index]?.dataNascimento}
                    onChange={(val: Date) => {
                      setValue(`filhos.${index}.dataNascimento`, val);
                    }}
                    maxDate={new Date()}
                    disabled={isClt}
                    isError={
                      !!errors.filhos && !filhosValue[index]?.dataNascimento
                    }
                  />
                </S.Field>
                <S.Field>
                  <p>Sexo</p>
                  <SelectComponent
                    data={SexoBiologicoOptions}
                    value={filhosValue[index].sexo}
                    onChange={(val: SexoBiologico) => {
                      setValue(`filhos.${index}.sexo`, val);
                    }}
                    disabled={isClt}
                    isError={
                      !!errors.filhos && filhosValue[index]?.sexo === undefined
                    }
                    renderLabel
                  />
                </S.Field>
              </S.Row>
            ))}
        </S.Form>

        <S.Row>
          <div id="dummy" />
          <S.Row>
            <SecondaryButton content="Cancelar" onClick={onClose} />
            <PrimaryButton
              content={saving ? <Loading size={18} color="white" /> : "Salvar"}
              onClick={() =>
                isClt ? onSubmitClt() : handleSubmit(onSubmit, onError)()
              }
              disabled={saving}
            />
          </S.Row>
        </S.Row>
      </Styles.Container>
    </Styles.DialogContainer>
  );
};
